/**
 * Column definitions for the Expenses DataTable
 */

import { PencilIcon, TrashIcon, WalletIcon } from 'lucide-react';

import { EmptyCell, TableColumn } from '@/components/common/DataTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ReadExpense } from '@/services/api/ledgerService';

import { formatCurrency } from '../shared/formatUtils';

interface ExpenseColumnActions {
  onEdit: (expense: ReadExpense) => void;
  onEditAllocations: (expense: ReadExpense) => void;
  onDelete: (expense: ReadExpense) => void;
}

/**
 * Render allocation status badge
 */
const renderStatusBadge = (expense: ReadExpense) => {
  const hasAllocations = expense.allocations.length > 0;
  const totalAllocated = expense.allocations.reduce(
    (sum, alloc) => sum + parseFloat(alloc.amount),
    0,
  );
  const totalBilled = parseFloat(expense.billed_amount);
  const hasUnallocated = expense.allocations.some(
    (a) => a.allocation_bucket === 'UNALLOCATED',
  );

  // Fully unallocated
  if (
    !hasAllocations ||
    totalAllocated === 0 ||
    (hasUnallocated && expense.allocations.length === 1)
  ) {
    return (
      <Badge
        variant="outline"
        className="bg-slate-100 text-slate-600 border-slate-200"
      >
        Unallocated
      </Badge>
    );
  }

  // Check if balanced
  const isBalanced = Math.abs(totalBilled - totalAllocated) < 0.01;

  // Partial - has UNALLOCATED bucket OR doesn't balance
  if (hasUnallocated || !isBalanced) {
    return (
      <Badge
        variant="outline"
        className="bg-amber-50 text-amber-700 border-amber-200"
      >
        Partial
      </Badge>
    );
  }

  // Fully allocated - no UNALLOCATED bucket AND balanced
  return (
    <Badge
      variant="outline"
      className="bg-emerald-50 text-emerald-700 border-emerald-200"
    >
      Allocated
    </Badge>
  );
};

/**
 * Create expense columns with action handlers
 */
export const createExpenseColumns = (
  actions: ExpenseColumnActions,
): TableColumn<ReadExpense>[] => [
  {
    id: 'service_date',
    header: 'Date',
    accessorKey: 'service_date',
    sortable: true,
    width: '110px',
    cell: (row) => (
      <div className="text-sm text-slate-900">
        {new Date(row.service_date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}
      </div>
    ),
  },
  {
    id: 'provider',
    header: 'Provider',
    accessorFn: (row) => row.provider?.name ?? '',
    sortable: true,
    cell: (row) => {
      if (!row.provider?.name) {
        return <EmptyCell />;
      }
      return (
        <div className="text-sm text-slate-900 truncate font-medium">
          {row.provider.name}
        </div>
      );
    },
  },
  {
    id: 'billed_amount',
    header: 'Billed',
    accessorKey: 'billed_amount',
    sortable: true,
    align: 'right',
    width: '100px',
    cell: (row) => (
      <div className="text-sm text-slate-900 font-semibold tabular-nums">
        {formatCurrency(row.billed_amount)}
      </div>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    sortable: false,
    width: '100px',
    cell: (row) => renderStatusBadge(row),
  },
  {
    id: 'actions',
    header: '',
    width: '50px',
    align: 'right',
    hideable: false,
    cell: (row) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="sr-only">Open menu</span>
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
              />
            </svg>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              actions.onEdit(row);
            }}
          >
            <PencilIcon className="mr-2 h-4 w-4" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              actions.onEditAllocations(row);
            }}
          >
            <WalletIcon className="mr-2 h-4 w-4" />
            Who paid?
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              actions.onDelete(row);
            }}
            className="text-red-600 focus:text-red-600"
          >
            <TrashIcon className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  },
];
